import { EntityAvatar } from '@/modules/common';
import { formatWallClockDate } from '@/utils/date-formatters';

import { formatHours } from '../../../helpers/format-time-track.helper';
import { ApprovalTimeEntry } from '../../../types/time-tracking-approvals';
import { DetailRow, ProjectColorDot, TranslateFn } from './approval-confirm-card';

interface SingleEntryCardProps {
  entry: ApprovalTimeEntry;
  /** i18n key prefix — `approvals.approveModal` or `approvals.unapproveModal`. */
  ns: string;
  t: TranslateFn;
}

/** Detail card for a single worklog entry (worklog-level, non-bulk approval). */
export function SingleEntryCard({ entry, ns, t }: SingleEntryCardProps) {
  return (
    <div className="mb-7 rounded-[10px] border border-input bg-muted/40 px-5 py-[18px]">
      <div className="mb-4 flex items-center gap-2">
        {/* `projectColorHex` is already normalized (with leading '#') by the approvals mapper. */}
        <ProjectColorDot color={entry.projectColorHex} />
        <span className="text-[17px] font-bold">{entry.projectName}</span>
      </div>

      <div className="flex flex-col gap-2.5">
        <DetailRow label={t(`${ns}.detailLabels.date` as Parameters<typeof t>[0])}>
          {formatWallClockDate(entry.date)}
        </DetailRow>

        <DetailRow label={t(`${ns}.detailLabels.member` as Parameters<typeof t>[0])}>
          <span className="flex items-center gap-1.5">
            <EntityAvatar image={entry.memberAvatarUrl} name={entry.memberName} size="xs" />
            {entry.memberName}
          </span>
        </DetailRow>

        <DetailRow label={t(`${ns}.detailLabels.hours` as Parameters<typeof t>[0])}>
          {formatHours(entry.trackedHours)}
        </DetailRow>

        <DetailRow
          label={t(`${ns}.detailLabels.approvedForInvoiceHours` as Parameters<typeof t>[0])}
        >
          {formatHours(entry.approvedForInvoiceHours)}
        </DetailRow>

        <DetailRow label={t(`${ns}.detailLabels.approvedForBillHours` as Parameters<typeof t>[0])}>
          {formatHours(entry.approvedForBillHours)}
        </DetailRow>

        {entry.approvalNotes && (
          <div className="flex min-w-0 flex-col gap-0.5 text-sm">
            <span className="text-muted-foreground">
              {t(`${ns}.detailLabels.approvalNotes` as Parameters<typeof t>[0])}
            </span>
            {/* Long, space-less notes must wrap instead of overflowing the modal.
                Vertical overflow is handled by the single body scroll container. */}
            <span className="whitespace-pre-wrap break-words font-semibold">
              {entry.approvalNotes}
            </span>
          </div>
        )}

        {entry.description && (
          <div className="flex min-w-0 flex-col gap-0.5 text-sm">
            <span className="text-muted-foreground">
              {t(`${ns}.detailLabels.description` as Parameters<typeof t>[0])}
            </span>
            <span className="whitespace-pre-wrap break-words font-semibold">
              {entry.description}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
